import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Calendar } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitContact } from "@/lib/api";

const types = [
  "Séance individuelle (1h15)",
  "Séance approfondie (2h)",
  "Accompagnement sur plusieurs séances",
  "Constellation en groupe (participant)",
  "Constellation en groupe (représentant)",
  "Autre demande",
];

export default function BookingDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    type_demande: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await submitContact({
        nom: form.nom.trim(),
        email: form.email.trim(),
        telephone: form.telephone.trim() || null,
        type_demande: form.type_demande || null,
        message: form.message.trim(),
      });
      toast.success("Merci, votre demande a bien été envoyée. Vous serez recontacté(e) rapidement.");
      setForm({ nom: "", email: "", telephone: "", type_demande: "", message: "" });
      onOpenChange(false);
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="sm:max-w-lg bg-[#FAF7F8] border-[#DDD7E2] rounded-3xl p-0 overflow-hidden"
      >
        <div className="p-7 md:p-8">
          <DialogHeader className="text-left space-y-2">
            <span className="text-xs uppercase tracking-[0.22em] text-[#C98FA3] font-medium">
              — Prise de rendez-vous
            </span>
            <DialogTitle className="font-serif-display text-3xl md:text-4xl font-light text-[#1E1E2A]">
              Prendre rendez-vous
            </DialogTitle>
            <DialogDescription className="text-sm text-[#5F6170]">
              Renseignez votre demande, vous serez recontacté(e) dans les plus brefs délais.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="booking-form">
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                Nom complet*
              </label>
              <Input
                data-testid="booking-nom"
                value={form.nom}
                onChange={update("nom")}
                placeholder="Votre nom"
                className="mt-2 bg-white border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl h-11"
                disabled={loading}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                  Email*
                </label>
                <Input
                  data-testid="booking-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="votre@email.com"
                  className="mt-2 bg-white border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl h-11"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                  Téléphone
                </label>
                <Input
                  data-testid="booking-telephone"
                  value={form.telephone}
                  onChange={update("telephone")}
                  placeholder="Facultatif"
                  className="mt-2 bg-white border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl h-11"
                  disabled={loading}
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                Type de demande
              </label>
              <Select
                value={form.type_demande}
                onValueChange={(v) => setForm((f) => ({ ...f, type_demande: v }))}
                disabled={loading}
              >
                <SelectTrigger
                  data-testid="booking-type"
                  className="mt-2 bg-white border-[#DDD7E2] rounded-xl h-11"
                >
                  <SelectValue placeholder="Choisir un accompagnement" />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                Votre message*
              </label>
              <Textarea
                data-testid="booking-message"
                value={form.message}
                onChange={update("message")}
                placeholder="Décrivez en quelques mots votre demande…"
                className="mt-2 bg-white border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl min-h-[110px]"
                disabled={loading}
              />
            </div>

            <a
              href="#booking"
              data-testid="booking-calendly-placeholder"
              className="block text-xs text-[#5F6170] underline underline-offset-4 hover:text-[#2847B8]"
            >
              Ou réserver directement en ligne (lien à venir)
            </a>

            <button
              type="submit"
              disabled={loading}
              data-testid="booking-submit"
              className="w-full bg-[#2847B8] text-white hover:bg-[#3A2E6E] disabled:opacity-60 transition-all duration-300 rounded-full h-12 font-medium inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Envoi…
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4" /> Envoyer ma demande
                </>
              )}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
